// GenericDataTable.jsx
import React, { useMemo, useState } from "react";
import "./GenericDataTable.css";

/** Turn "total_revenue" -> "Total Revenue", "itemName" -> "Item Name" */
function prettifyLabel(key) {
  if (!key) return "";
  const withSpaces = key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2");
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
}

/** Heuristic: is this column mostly numeric? */
function isNumericColumn(rows, key) {
  let numericCount = 0;
  let checked = 0;
  for (let i = 0; i < rows.length && checked < 20; i++) {
    const v = rows[i][key];
    if (v === null || v === undefined || v === "") continue;
    checked++;
    if (!isNaN(Number(v))) numericCount++;
  }
  return checked > 0 && numericCount / checked > 0.7;
}

/** Compute color for Status cell using Quantity In-Stock + Warning Level */
function getStatusColor(row) {
  const qty = Number(row["Quantity In-Stock"]);
  const warn = Number(row["Warning Level"]);

  if (!Number.isFinite(qty)) return undefined;

  if (qty === 0) return "#d32f2f"; // red
  if (Number.isFinite(warn) && warn > 0 && qty <= warn) return "#ff9800"; // amber

  return "#2e7d32"; // green
}

export default function GenericDataTable({
  rows,
  defaultSortKey = null,
  defaultSortDirection = "desc",
  maxHeight = "60vh",
}) {
  const [filterText, setFilterText] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: defaultSortKey,
    direction: defaultSortDirection,
  });

  const columns = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    const keys = Object.keys(rows[0]);
    return keys.map((key) => ({
      key,
      label: prettifyLabel(key),
      numeric: isNumericColumn(rows, key),
    }));
  }, [rows]);

  const filteredAndSorted = useMemo(() => {
    if (!rows) return [];

    let data = [...rows];

    // filter
    if (filterText.trim()) {
      const needle = filterText.toLowerCase();
      data = data.filter((row) =>
        columns.some((col) => {
          const v = row[col.key];
          return (
            v !== null &&
            v !== undefined &&
            String(v).toLowerCase().includes(needle)
          );
        })
      );
    }

    // sort
    if (sortConfig.key) {
      const { key, direction } = sortConfig;
      data.sort((a, b) => {
        const A = a[key];
        const B = b[key];

        if (A == null && B == null) return 0;
        if (A == null) return 1;
        if (B == null) return -1;

        const numA = Number(A);
        const numB = Number(B);
        const bothNumeric = !isNaN(numA) && !isNaN(numB);

        if (bothNumeric) {
          return direction === "asc" ? numA - numB : numB - numA;
        }

        const sA = String(A).toLowerCase();
        const sB = String(B).toLowerCase();
        if (sA < sB) return direction === "asc" ? -1 : 1;
        if (sA > sB) return direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return data;
  }, [rows, columns, filterText, sortConfig]);

  function handleSort(colKey) {
    setSortConfig((prev) => {
      if (prev.key === colKey) {
        return {
          key: colKey,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key: colKey, direction: "asc" };
    });
  }

  if (!rows || rows.length === 0) return <div>No data</div>;

  return (
    <div>
      {/* Filter Bar */}
      <div className="gdt-filter-bar">
        <input
          type="text"
          placeholder="Filter rows…"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="gdt-filter-input"
        />
        <span className="gdt-row-count">
          {filteredAndSorted.length} row(s)
        </span>
      </div>

      {/* Scroll container */}
      <div className="gdt-scroll" style={{ maxHeight }}>
        <table className="gdt-table">
          <thead>
            <tr>
              {columns.map((col) => {
                const isSorted = sortConfig.key === col.key;
                const arrow =
                  !isSorted ? "↕" : sortConfig.direction === "asc" ? "▲" : "▼";

                return (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className={`gdt-th ${col.numeric ? "gdt-th-right" : "gdt-th-left"}`}
                  >
                    <span className="gdt-th-label">
                      {col.label}
                      <span className="gdt-arrow">{arrow}</span>
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {filteredAndSorted.map((row, idx) => (
              <tr key={idx} className="gdt-row">
                {columns.map((col) => {
                  const value =
                    row[col.key] !== null && row[col.key] !== undefined
                      ? String(row[col.key])
                      : "";

                  if (col.key === "Status") {
                    const color = getStatusColor(row);
                    return (
                      <td
                        key={col.key}
                        className={`gdt-status ${
                          col.numeric ? "gdt-td-right" : "gdt-td-left"
                        }`}
                        style={{ color: color || "inherit" }}
                      >
                        {value}
                      </td>
                    );
                  }

                  return (
                    <td
                      key={col.key}
                      className={`gdt-td ${
                        col.numeric ? "gdt-td-right" : "gdt-td-left"
                      }`}
                    >
                      {value}
                    </td>
                  );
                })}
              </tr>
            ))}

            {filteredAndSorted.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="gdt-empty-row"
                >
                  No results
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="gdt-footer-note">
        On small screens you can scroll this table horizontally.
      </div>
    </div>
  );
}
